import { mdiCreditCardPlusOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, ButtonBase, InputAdornment, TextField, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { NumberFormatValues, NumericFormat, SourceInfo } from 'react-number-format';

import { Colors } from '../../../shared/colors';

interface Props {
  saldo: number | '';
  adicionarSaldo: (valor: number | '') => void;
}

const valoresSugeridos = [50, 100, 150, 200];
const valorMaximo = 99999.99;
const formatarValor = (valor: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    valor
  );

const AdicionarSaldo = ({ saldo, adicionarSaldo: setSaldo }: Props) => {
  const outroValorInicial =
    typeof saldo === 'number' && saldo > 0 && !valoresSugeridos.includes(saldo);
  const [valorDigitado, setValorDigitado] = useState<number | ''>(outroValorInicial ? saldo : '');
  const [usandoOutroValor, setUsandoOutroValor] = useState(outroValorInicial);
  const [error, setError] = useState<string>();

  const selecionarValor = useCallback(
    (valor: number) => {
      setUsandoOutroValor(false);
      setValorDigitado('');
      setError('');
      setSaldo(valor);
    },
    [setSaldo]
  );

  const onValueChange = useCallback(
    (values: NumberFormatValues, sourceInfo: SourceInfo) => {
      if (sourceInfo.source !== 'event') return;

      const valor = values.floatValue;
      setValorDigitado(valor ?? '');
      setSaldo(valor && valor > 0 ? valor : '');
      setError(valor === undefined ? '' : valor <= 0 ? 'Informe um valor maior que zero' : '');
    },
    [setSaldo]
  );

  const onInputFocus = useCallback(() => {
    if (!usandoOutroValor) {
      setUsandoOutroValor(true);
      setSaldo('');
    }
  }, [setSaldo, usandoOutroValor]);

  return (
    <Box width="100%" display="flex" flexDirection="column" alignItems="center">
      <Box
        width={60}
        height={60}
        borderRadius="50%"
        bgcolor={Colors.primary.light}
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={1.5}
      >
        <Icon path={mdiCreditCardPlusOutline} size={1.45} color={Colors.primary.main} />
      </Box>

      <Typography color={Colors.secondary} fontSize={20} fontWeight={700} textAlign="center">
        Quanto você deseja adicionar?
      </Typography>
      <Typography color={Colors.text.label} fontSize={14} textAlign="center" mb={2.5}>
        Escolha uma sugestão ou informe outro valor.
      </Typography>

      <Box
        width="100%"
        maxWidth={320}
        minHeight={64}
        borderRadius={2}
        bgcolor={Colors.primary.light}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        mb={2}
      >
        <Typography color={Colors.primary.dark} fontSize={14} fontWeight={600}>
          R$
        </Typography>
        <Typography color={Colors.primary.dark} fontSize={{ xs: 28, sm: 32 }} fontWeight={700}>
          {formatarValor(Number(saldo) || 0)}
        </Typography>
      </Box>

      <Box width="100%" display="grid" gridTemplateColumns="repeat(4, minmax(0, 1fr))" gap={1}>
        {valoresSugeridos.map((valor) => {
          const selecionado = !usandoOutroValor && saldo === valor;

          return (
            <ButtonBase
              key={valor}
              onClick={() => selecionarValor(valor)}
              aria-pressed={selecionado}
              sx={{
                minHeight: 48,
                border: `1px solid ${selecionado ? Colors.primary.main : Colors.border.divider}`,
                borderRadius: 1,
                bgcolor: selecionado ? Colors.primary.light : Colors.white,
                color: selecionado ? Colors.primary.dark : Colors.text.label,
                fontSize: { xs: 12, sm: 14 },
                fontWeight: 700,
                whiteSpace: 'nowrap',
                '&.Mui-focusVisible': { outline: `2px solid ${Colors.primary.main}` }
              }}
            >
              + R$ {valor}
            </ButtonBase>
          );
        })}
      </Box>

      <Box width="100%" mt={3}>
        <Typography color={Colors.secondary} fontSize={14} mb={1}>
          Ou informe outro valor
        </Typography>
        <NumericFormat
          fullWidth
          size="small"
          decimalScale={2}
          fixedDecimalScale
          allowNegative={false}
          isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= valorMaximo}
          value={valorDigitado}
          decimalSeparator=","
          thousandSeparator="."
          customInput={TextField}
          onFocus={onInputFocus}
          onValueChange={onValueChange}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  alignSelf: 'stretch',
                  backgroundColor: '#F5F6F8',
                  borderRight: '1px solid #DDE2E8',
                  height: 'auto',
                  maxHeight: 'none',
                  ml: -1.75,
                  mr: 1.5,
                  px: 1.25,
                  '& .MuiTypography-root': { color: '#667085', fontSize: 13, fontWeight: 400 }
                }}
              >
                R$
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              borderRadius: 1,
              height: 40,
              overflow: 'hidden',
              '& fieldset': { borderColor: '#C8D0DA' },
              '&:hover fieldset': { borderColor: '#C8D0DA' },
              '&.Mui-focused fieldset': { borderColor: '#C8D0DA', borderWidth: 1 }
            },
            '& .MuiInputBase-input': { color: '#667085', fontSize: 13, fontWeight: 400 },
            '& .MuiInputBase-input::placeholder': { color: '#98A2B3', opacity: 1 }
          }}
          placeholder="Informe o valor"
          inputProps={{ 'aria-label': 'Outro valor para adicionar' }}
        />
        {error && (
          <Typography color={Colors.error.red} fontSize={13} mt={0.75}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AdicionarSaldo;
